// Single source of truth for the on-disk car format. A car is a
// tunable instance of the physics spec — the fields here mirror what
// `physicsStep` reads, plus livery + display metadata. Storage is
// metric/SI throughout (kg, W, N, rad, m).
//
// Forward compatibility: every spec field added in a new version gets
// a sane default in CAR_SPEC_DEFAULTS, and loadCar() backfills missing
// fields so old saves keep loading. The simulator imports
// CAR_SPEC_DEFAULTS from here so spec defaults never drift between
// schema and runtime.

export const FORMAT_VERSION = 1;

// Physics spec defaults. Used as the fallback when a car file omits a
// field, and re-exported from simulator/src/world.js as
// DEFAULT_CAR_SPEC so the runtime sees the same numbers.
export const CAR_SPEC_DEFAULTS = {
  mass:              700,      // kg
  enginePower:       300000,   // W (300 kW ~ 400 hp)
  brakeForce:        18000,    // N
  maxSteeringAngle:  0.45,     // rad (~26°) — absolute max at low speed
  steeringRate:      4.0,      // rad/s — how fast the wheel can move
  steerSpeedRef:     16.7,     // m/s (~60 km/h) — speed at which authority halves
  minSteerMul:       0.18,     // floor multiplier at very high speeds
  grip:              1.4,      // g — LATERAL grip (cornering)
  tractionGrip:      1.8,      // g — LONGITUDINAL grip; higher than lateral on race tires
  dragCoeff:         0.9,      // quadratic aero drag
  rollingResistance: 30,       // N per (m/s) — small linear drag
  wheelbase:         2.7,      // m
  yawInertia:        1100,     // kg·m² — chassis resistance to rotation
  frontGripBias:     0.47,     // 0..1 — share of grip on front. <0.5 = understeer
  corneringStiffness: 10,      // how quickly tires build force with slip angle
  yawDamping:        0.6,      // suspension/aero stabilization stand-in
  carLength:         4.6,
  carWidth:          1.9,

  // ---- condition / consumables ----
  // Tire compound — picked from shared/tire-compounds.js. Drives the
  // grip multiplier and wear rate. For now this is a chassis spec;
  // when pit-stop strategy lands we'll move per-stint compound choice
  // to car.condition so the rubber can change without rebuilding the
  // car.
  tireCompound:      'medium',
  // Fuel tank capacity (kg). 50 kg ≈ 70 L of gasoline — F1-ish race
  // pace. Heavier classes (sports cars / endurance) use more, lighter
  // classes (single-seaters in low-fuel trim) use less.
  fuelCapacity:      50,
  // Fuel consumption at full throttle (kg/s). Linear in throttle so
  // half-throttle burns half. 0.05 kg/s = 180 g/min, sized so a 50 kg
  // tank lasts ~17 min of constant full-throttle running — about a
  // 60-lap stint at GT pace, longer in mixed throttle.
  fuelBurnRate:      0.05,
  // Damage durability multiplier — divides the per-impact damage
  // computation. 1.0 = baseline (open-wheeler-fragile); 2.0 = takes
  // half damage from the same hit (NASCAR-style stock car); 0.7 = takes
  // ~40 % more (carbon-fiber prototype). The car becomes inoperable
  // once damage > 0.5 regardless of durability — this just changes how
  // many hits it takes to get there.
  durability:        1.0,
};

export function newEmptyCar() {
  return {
    formatVersion: FORMAT_VERSION,
    name: '',
    description: '',
    manufacturer: '',
    spec: { ...CAR_SPEC_DEFAULTS },
    livery: { primary: '#cc0000', accent: '#ffffff' },
    metadata: { createdAt: null, modifiedAt: null },
  };
}

// Validate + normalize a parsed car object. Backfills any missing spec
// fields from CAR_SPEC_DEFAULTS so callers always get a fully-populated
// runtime-ready spec. Returns { car, formatMismatch }.
export function loadCar(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Not a valid car file: not an object');
  }
  if (typeof data.formatVersion !== 'number') {
    throw new Error('Not a valid car file: missing formatVersion');
  }

  const merged = newEmptyCar();
  Object.assign(merged, data);

  // Spec gets a deep-ish merge so a partial spec override (e.g. a tune
  // file that only changes mass + grip) still produces a complete spec.
  merged.spec = { ...CAR_SPEC_DEFAULTS, ...(data.spec ?? {}) };

  merged.livery = { ...newEmptyCar().livery, ...(data.livery ?? {}) };
  if (typeof merged.name !== 'string') merged.name = '';
  if (typeof merged.description !== 'string') merged.description = '';
  if (typeof merged.manufacturer !== 'string') merged.manufacturer = '';
  if (!merged.metadata) merged.metadata = { createdAt: null, modifiedAt: null };

  return {
    car: merged,
    formatMismatch: data.formatVersion > FORMAT_VERSION ? data.formatVersion : null,
  };
}
